import { FormProvider, useForm } from 'react-hook-form'
import { CurecallButton, ECurecallButtonAppearance } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import { TNodeFormValues } from '../node.type'
import { ELocale, PREFERED_LANGUAGE } from 'src/types/locales'
import { useChatbotFlow } from '../chatbot.provider'
import { NodeTitleAndLanguages } from './node-title-and-languages-fields.component'
import { NodeVariableFields } from './node-variable-fields.component'

export function NodeForm() {
  const { t } = useTranslation()
  const {
    isFormOpen,
    selectedNodeId,
    addNode,
    updateNode,
    deleteNode,
    closeForm,
    nodes,
    saveChatbot,
    setFormDirty,
  } = useChatbotFlow()

  const methods = useForm<TNodeFormValues>({
    defaultValues: {
      title: '',
      messagesByLanguage: { [PREFERED_LANGUAGE]: '' },
      variable: undefined,
      clarificationMessage: undefined,
      humanReviewCheck: undefined,
    },
  })
  const {
    reset,
    handleSubmit,
    formState: { isDirty },
  } = methods

  useEffect(() => {
    if (isFormOpen && selectedNodeId) {
      const node = nodes.find((n) => n.id === selectedNodeId)
      if (node) {
        reset({
          title: node.data.title,
          messagesByLanguage: node.data.messagesByLanguage,
          variable: node.data.variable,
          clarificationMessage: node.data.clarificationMessage,
          humanReviewCheck: node.data.humanReviewCheck,
        })
      }
    } else if (isFormOpen) {
      reset({
        title: '',
        messagesByLanguage: { [ELocale.FR]: '' },
        variable: undefined,
        clarificationMessage: undefined,
        humanReviewCheck: undefined,
      })
    }
  }, [isFormOpen, selectedNodeId, nodes, reset])

  useEffect(() => {
    setFormDirty(isDirty)
  }, [isDirty, setFormDirty])

  const onSubmit = async (data: TNodeFormValues) => {
    if (selectedNodeId) {
      updateNode(selectedNodeId, data)
    } else {
      addNode(data)
    }
    await saveChatbot()
    closeForm()
  }

  const handleDeleteNode = async () => {
    if (selectedNodeId) {
      deleteNode(selectedNodeId)
      await saveChatbot()
      closeForm()
    }
  }

  if (!isFormOpen) {
    return null
  }

  if (!selectedNodeId) {
    return (
      <div className="c-flex-1 c-h-full c-px-6 c-flex c-flex-col c-items-center c-justify-center">
        <p className="c-text-gray-500 c-text-center">
          {t('pages.configuration.followups.chatbot.node-form.select-node-message')}
        </p>
      </div>
    )
  }

  return (
    <div className="c-flex-1 c-h-full c-px-6 c-flex c-flex-col">
      <h3 className="c-text-lg c-font-semibold c-text-gray-900 c-mb-2">
        {t('pages.configuration.followups.chatbot.node-form.node-configuration')}
      </h3>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="c-flex-1 c-flex c-flex-col c-gap-6">
          <NodeTitleAndLanguages />
          <NodeVariableFields />
          <div className="c-flex c-justify-center c-gap-3 c-mb-6 c-mt-auto">
            <CurecallButton appearance={ECurecallButtonAppearance.CANCEL} type="button" onClick={closeForm}>
              {t('pages.configuration.followups.chatbot.node-form.cancel')}
            </CurecallButton>
            <CurecallButton
              appearance={ECurecallButtonAppearance.ALERT}
              type="button"
              onClick={handleDeleteNode}
            >
              {t('pages.configuration.followups.chatbot.node-form.delete-node')}
            </CurecallButton>
            <CurecallButton type="submit">
              {t('pages.configuration.followups.chatbot.node-form.validate-node')}
            </CurecallButton>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
